<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">QA / Test Cases</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Author test cases and record execution results per project.</p>
      </div>
      <button type="button" @click="openCreate"
        class="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-amber-400 px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition active:scale-95">
        <v-icon size="18">mdi-plus</v-icon>New Case
      </button>
    </div>

    <!-- Filters -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <v-autocomplete density="compact" variant="solo-filled" flat hide-details clearable label="Project" :items="projectItems" item-title="title" item-value="value" v-model="filters.project_id" @update:modelValue="fetchCases"></v-autocomplete>
        <v-select density="compact" variant="solo-filled" flat hide-details clearable label="Result" :items="resultFilterItems" v-model="filters.result"></v-select>
        <v-autocomplete density="compact" variant="solo-filled" flat hide-details clearable label="Assignee" :items="assigneeItems" item-title="title" item-value="value" v-model="filters.assignee_id" @update:modelValue="fetchCases"></v-autocomplete>
        <v-text-field density="compact" variant="solo-filled" flat hide-details placeholder="Search…" prepend-inner-icon="mdi-magnify" v-model="search"></v-text-field>
      </div>
      <div class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <v-text-field density="compact" variant="solo-filled" flat hide-details clearable type="date" label="Created from" v-model="dateFrom"></v-text-field>
        <v-text-field density="compact" variant="solo-filled" flat hide-details clearable type="date" label="Created to" v-model="dateTo"></v-text-field>
        <button type="button" @click="overdueOnly = !overdueOnly"
          class="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition active:scale-95"
          :class="overdueOnly ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'">
          <v-icon size="16">mdi-alert-circle-outline</v-icon> Overdue only
        </button>
      </div>
    </div>

    <!-- Result summary -->
    <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <StatCard v-for="r in resultItems" :key="r" :label="r" :value="countByResult(r)" :color="statusColor(r)" />
      <StatCard label="Overdue" :value="overdueCount" color="error" />
    </div>

    <!-- Test case list -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <!-- Loading -->
      <div v-if="loading && !cases.length" class="space-y-2">
        <div v-for="n in 6" :key="'ql-' + n" class="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
      </div>

      <!-- Empty -->
      <EmptyState v-else-if="!filteredCases.length" icon="mdi-test-tube-empty" title="No test cases yet." dense />

      <!-- Cards (search reuses the existing filterRow method) -->
      <div v-else class="space-y-2">
        <div v-for="item in filteredCases" :key="item.id" v-show="filterRow(null, search, { raw: item })"
          class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
          <!-- Header: project/ticket/overdue + priority/result -->
          <div class="flex items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-1.5">
              <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">{{ item.project ? item.project.key : '—' }}</span>
              <span v-if="item.ticket_id" class="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">{{ item.ticket_id }}</span>
              <span v-if="isOverdue(item)" class="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Overdue</span>
            </div>
            <div class="flex shrink-0 items-center gap-1.5">
              <PriorityChip :priority="item.priority" />
              <StatusChip :status="item.latest_result" size="x-small" />
            </div>
          </div>

          <!-- Title (opens detail) -->
          <button type="button" @click="openDetail(item)" class="mt-1.5 block w-full text-left">
            <span class="text-sm font-semibold leading-snug text-gray-900 hover:underline dark:text-gray-50">{{ item.title }}</span>
          </button>

          <!-- Meta: assignee · due · last run -->
          <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span class="inline-flex items-center gap-1"><v-icon size="13">mdi-account-outline</v-icon>{{ item.assignee_name || 'Unassigned' }}</span>
            <span v-if="item.due_date" class="inline-flex items-center gap-1" :class="isOverdue(item) ? 'font-semibold text-rose-600 dark:text-rose-400' : ''">
              <v-icon size="13">mdi-calendar-clock</v-icon>{{ formattedDate(item.due_date) }}
            </span>
            <span v-if="item.last_run_at" class="inline-flex items-center gap-1"><v-icon size="13">mdi-history</v-icon>{{ formattedDate(item.last_run_at) }}</span>
          </div>

          <!-- Footer: actions -->
          <div class="mt-2.5 flex items-center justify-end gap-1 border-t border-gray-100 pt-2 dark:border-gray-800">
            <button type="button" aria-label="Record execution" @click="openExecute(item)"
              class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-play-circle-outline</v-icon></button>
            <button type="button" aria-label="Edit test case" @click="openEdit(item)"
              class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-pencil-outline</v-icon></button>
            <button type="button" aria-label="Transfer test case" @click="openTransfer(item)"
              class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-account-switch-outline</v-icon></button>
            <button type="button" aria-label="Delete test case" @click="askDelete(item)"
              class="rounded-lg p-1.5 text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/10"><v-icon size="18">mdi-delete-outline</v-icon></button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / edit dialog -->
    <v-dialog v-model="dialog" max-width="680">
      <v-card :title="editing ? 'Edit Test Case' : 'New Test Case'" prepend-icon="mdi-test-tube">
        <v-card-text>
          <v-text-field density="compact" label="Title*" v-model="form.title"></v-text-field>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-autocomplete density="compact" label="Project*" :items="projectSelectItems" item-title="title" item-value="value" v-model="form.project_id" @update:modelValue="onFormProjectChange"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete density="compact" label="Linked ticket (optional)" :items="ticketItems" item-title="title" item-value="value" v-model="form.ticket_id" :disabled="!form.project_id" :loading="ticketsLoading" clearable :no-data-text="form.project_id ? 'No tickets in this project' : 'Select a project first'"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select density="compact" label="Priority" :items="priorityItems" v-model="form.priority"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete density="compact" label="Assignee" :items="assigneeSelectItems" item-title="title" item-value="value" v-model="form.assignee_id" clearable></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field density="compact" type="date" label="Due date*" prepend-inner-icon="mdi-calendar-clock" v-model="form.due_date"></v-text-field>
            </v-col>
          </v-row>
          <v-textarea density="compact" label="Preconditions" rows="2" v-model="form.preconditions"></v-textarea>
          <v-textarea density="compact" label="Steps" rows="4" v-model="form.steps"></v-textarea>
          <v-textarea density="compact" label="Expected result" rows="2" v-model="form.expected_result"></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="editing" color="error" variant="text" prepend-icon="mdi-delete" @click="remove">Delete</v-btn>
          <v-spacer></v-spacer>
          <v-btn text="Close" variant="plain" @click="dialog = false"></v-btn>
          <v-btn color="primary" text="Save" variant="flat" :disabled="!form.title || !form.project_id || !form.due_date" @click="save"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail dialog -->
    <v-dialog v-model="detailDialog" max-width="720">
      <v-card v-if="detail" prepend-icon="mdi-test-tube" :title="detail.title">
        <v-card-text>
          <div class="d-flex flex-wrap mb-3" style="gap: 8px">
            <v-chip size="small" variant="tonal">{{ detail.project ? detail.project.key : '—' }}</v-chip>
            <v-chip size="small" v-if="detail.ticket_id" variant="tonal" color="info">{{ detail.ticket_id }}</v-chip>
            <PriorityChip :priority="detail.priority" />
            <StatusChip :status="detail.latest_result" />
            <v-chip size="small" variant="tonal" prepend-icon="mdi-account">{{ detail.assignee_name || 'Unassigned' }}</v-chip>
          </div>
          <div class="text-caption text-grey mb-3">
            Created {{ formattedDateTime(detail.created_at) }}<span v-if="detail.created_by"> by {{ detail.created_by }}</span>
            · Updated {{ formattedDateTime(detail.updated_at) }}<span v-if="detail.updated_by"> by {{ detail.updated_by }}</span>
          </div>

          <div class="qa-block" v-if="detail.preconditions">
            <div class="qa-label">Preconditions</div>
            <div class="qa-text">{{ detail.preconditions }}</div>
          </div>
          <div class="qa-block" v-if="detail.steps">
            <div class="qa-label">Steps</div>
            <div class="qa-text" style="white-space: pre-wrap">{{ detail.steps }}</div>
          </div>
          <div class="qa-block" v-if="detail.expected_result">
            <div class="qa-label">Expected result</div>
            <div class="qa-text">{{ detail.expected_result }}</div>
          </div>

          <v-divider class="my-3"></v-divider>
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="qa-label mb-0">Execution history</div>
            <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-play" @click="openExecute(detail)">Record execution</v-btn>
          </div>
          <div v-if="executionsLoading" class="text-center py-4">
            <v-progress-circular indeterminate size="22"></v-progress-circular>
          </div>
          <v-list v-else-if="executions.length" density="compact">
            <v-list-item v-for="ex in executions" :key="ex.id">
              <template v-slot:prepend>
                <v-icon :color="statusColor(ex.result)">mdi-circle-medium</v-icon>
              </template>
              <v-list-item-title>
                <span class="font-weight-medium">{{ ex.result }}</span>
                <span class="text-caption text-grey ml-2">{{ formattedDateTime(ex.created_at) }}</span>
              </v-list-item-title>
              <v-list-item-subtitle v-if="ex.notes">{{ ex.notes }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="ex.executed_by" class="text-caption">by {{ ex.executed_by }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <EmptyState v-else icon="mdi-play-circle-outline" title="No executions recorded yet." dense />

          <v-divider class="my-3"></v-divider>
          <div class="qa-label">Activity &amp; comments</div>
          <div class="d-flex align-center mb-3" style="gap: 8px">
            <v-text-field density="compact" hide-details placeholder="Add a comment…" v-model="commentText" @keyup.enter="addComment"></v-text-field>
            <v-btn color="primary" variant="tonal" :disabled="!commentText" @click="addComment">Post</v-btn>
          </div>
          <div v-if="activityLoading" class="text-center py-3">
            <v-progress-circular indeterminate size="22"></v-progress-circular>
          </div>
          <v-timeline v-else-if="activity.length" density="compact" side="end" class="qa-timeline">
            <v-timeline-item v-for="a in activity" :key="a.id" :dot-color="activityColor(a.action)" size="x-small">
              <div class="d-flex justify-space-between" style="gap: 12px">
                <div>
                  <span class="font-weight-medium">{{ a.action === 'comment' ? a.content : a.message }}</span>
                  <div class="text-caption text-grey">{{ a.actor_name }}</div>
                </div>
                <div class="text-caption text-grey text-no-wrap">{{ formattedDateTime(a.created_at) }}</div>
              </div>
            </v-timeline-item>
          </v-timeline>
          <EmptyState v-else icon="mdi-timeline-text-outline" title="No activity yet." dense />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Close" variant="plain" @click="detailDialog = false"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Record execution dialog -->
    <v-dialog v-model="executeDialog" max-width="480">
      <v-card prepend-icon="mdi-play-circle" title="Record Execution">
        <v-card-text>
          <div class="text-body-2 mb-3 text-grey">{{ executeForm.title }}</div>
          <v-select density="compact" label="Result*" :items="resultItems" v-model="executeForm.result"></v-select>
          <v-textarea density="compact" label="Notes" rows="3" v-model="executeForm.notes"></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Close" variant="plain" @click="executeDialog = false"></v-btn>
          <v-btn color="primary" text="Save" variant="flat" :disabled="!executeForm.result" @click="saveExecution"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card prepend-icon="mdi-alert" title="Delete test case?">
        <v-card-text>
          This will permanently delete <strong>{{ deleteTarget ? deleteTarget.title : '' }}</strong> and its execution history. This cannot be undone.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Cancel" variant="plain" @click="deleteDialog = false"></v-btn>
          <v-btn color="error" text="Delete" variant="flat" :loading="deleting" @click="confirmDelete"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <TransferDialog
      v-model="transferDialog"
      :item="transferTarget"
      :endpoint="constant.qa_transfer"
      id-key="id"
      entity-label="test case"
      :title="transferTarget ? transferTarget.title : ''"
      :employees="employees"
      :current-assignee-id="transferTarget ? transferTarget.assignee_id : null"
      :current-name="transferTarget ? transferTarget.assignee_name : ''"
      :updated-by="currentUserLabel()"
      @transferred="onTransferred"
    ></TransferDialog>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import Snackbar from './Snackbar.vue';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import StatCard from '@/components/ui/StatCard.vue';
import StatusChip from '@/components/ui/StatusChip.vue';
import PriorityChip from '@/components/ui/PriorityChip.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import TransferDialog from './TransferDialog.vue';

export default {
  name: 'QaTestCasesPage',
  components: { Snackbar, StatCard, StatusChip, PriorityChip, EmptyState, TransferDialog },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant: constant,
      loading: false,
      search: '',
      dateFrom: null,
      dateTo: null,
      overdueOnly: false,
      cases: [],
      projects: [],
      tickets: [],
      ticketsLoading: false,
      employees: [],
      user_data: null,
      isshow: false,
      message: '',
      dialog: false,
      editing: false,
      detailDialog: false,
      detail: null,
      executions: [],
      executionsLoading: false,
      activity: [],
      activityLoading: false,
      commentText: '',
      executeDialog: false,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      transferDialog: false,
      transferTarget: null,
      filters: { project_id: null, result: null, assignee_id: null },
      form: this.blankForm(),
      executeForm: { test_case_id: null, title: '', result: 'Pass', notes: '' },
      priorityItems: ['Low', 'Medium', 'High', 'Critical'],
      resultItems: ['Pass', 'Fail', 'Blocked', 'Skipped'],
      headers: [
        { title: 'Title', key: 'title', sortable: true },
        { title: 'Project', key: 'project', sortable: false },
        { title: 'Ticket', key: 'ticket_id', sortable: true },
        { title: 'Assignee', key: 'assignee_name', sortable: false },
        { title: 'Priority', key: 'priority', sortable: true },
        { title: 'Latest result', key: 'latest_result', sortable: true },
        { title: 'Due', key: 'due_date', sortable: true },
        { title: 'Last run', key: 'last_run_at', sortable: true },
        { title: 'Created by', key: 'created_by', sortable: true },
        { title: 'Updated by', key: 'updated_by', sortable: true },
        { title: 'Updated', key: 'updated_at', sortable: true },
        { title: '', key: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    projectItems() {
      return [{ value: null, title: 'All projects' }, ...this.projects.map(p => ({ value: p.id, title: `${p.key} — ${p.title}` }))];
    },
    projectSelectItems() {
      return this.projects.map(p => ({ value: p.id, title: `${p.key} — ${p.title}` }));
    },
    assigneeItems() {
      return [{ value: null, title: 'All' }, ...this.employees.map(e => ({ value: e.id, title: e.fullname }))];
    },
    assigneeSelectItems() {
      return this.employees.map(e => ({ value: e.id, title: e.fullname }));
    },
    ticketItems() {
      return this.tickets.map(t => ({ value: t.ticket_id, title: t.name ? `${t.ticket_id} — ${t.name}` : t.ticket_id }));
    },
    resultFilterItems() {
      return ['Untested', ...this.resultItems];
    },
    // Today as YYYY-MM-DD (local) for overdue comparison.
    today() {
      const d = new Date();
      const off = d.getTimezoneOffset();
      return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
    },
    filteredCases() {
      return this.cases.filter((c) => {
        if (this.filters.result && c.latest_result !== this.filters.result) return false;
        if (this.dateFrom || this.dateTo) {
          if (!c.created_at) return false;
          const d = String(c.created_at).slice(0, 10);
          if (this.dateFrom && d < this.dateFrom) return false;
          if (this.dateTo && d > this.dateTo) return false;
        }
        if (this.overdueOnly && !this.isOverdue(c)) return false;
        return true;
      });
    },
    overdueCount() {
      return this.cases.filter(c => this.isOverdue(c)).length;
    },
  },
  methods: {
    blankForm() {
      return { id: null, project_id: null, ticket_id: '', title: '', preconditions: '', steps: '', expected_result: '', priority: 'Medium', assignee_id: null, due_date: null };
    },
    // Overdue = has a due date strictly before today and not yet passed
    // (a passing test case is considered finished).
    isOverdue(item) {
      if (!item || !item.due_date) return false;
      if (item.latest_result === 'Pass') return false;
      return String(item.due_date).slice(0, 10) < this.today;
    },
    activityColor(action) {
      switch (action) {
        case 'created': return 'primary';
        case 'execution': return 'info';
        case 'assignee': return 'warning';
        case 'comment': return 'success';
        default: return 'grey';
      }
    },
    countByResult(r) {
      return this.cases.filter(c => c.latest_result === r).length;
    },
    filterRow(value, query, item) {
      if (!query) return true;
      const q = query.toLowerCase();
      const raw = item && item.raw ? item.raw : {};
      const hay = [raw.title, raw.ticket_id].join(' ').toLowerCase();
      return hay.includes(q);
    },
    notify(msg) {
      this.isshow = true;
      this.message = msg;
      setTimeout(() => { this.isshow = false; }, 2500);
    },
    errorMessage(error, fallback) {
      const res = error && error.response ? error.response.data : null;
      if (res) {
        if (res.errors) {
          const first = Object.values(res.errors)[0];
          if (Array.isArray(first) && first.length) return first[0];
        }
        if (res.message) return res.message;
      }
      return fallback;
    },
    buildParams() {
      const p = {};
      if (this.filters.project_id) p.project_id = this.filters.project_id;
      if (this.filters.assignee_id) p.assignee_id = this.filters.assignee_id;
      return p;
    },
    fetchCases() {
      this.loading = true;
      axios.get(utils._api(this.constant.qa_list), { ...this.authHeaders(), params: this.buildParams() })
        .then((response) => { this.cases = response.data.data || []; this.openPendingCase(); })
        .catch((error) => { console.error('Error fetching test cases:', error); this.notify('Failed to load test cases'); })
        .finally(() => { this.loading = false; });
    },
    // If we arrived here via a "open in QA" link from a ticket, open that case.
    openPendingCase() {
      const id = localStorage.getItem('qa_open_case_id');
      if (!id) return;
      localStorage.removeItem('qa_open_case_id');
      const match = this.cases.find(c => String(c.id) === String(id));
      if (match) this.openDetail(match);
    },
    getProjects() {
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => { this.projects = response.data.data || []; })
        .catch(error => console.error('Error fetching projects:', error));
    },
    getEmployees() {
      axios.get(utils._api(this.constant.get_active_users), this.authHeaders())
        .then((response) => { this.employees = response.data.data || []; })
        .catch(error => console.error('Error fetching users:', error));
    },
    // Load the linkable tickets for a given project (drives the form dropdown).
    fetchTicketsForProject(projectId) {
      if (!projectId) { this.tickets = []; return; }
      this.ticketsLoading = true;
      axios.get(utils._api(this.constant.get_tickets), { ...this.authHeaders(), params: { project_id: projectId } })
        .then((response) => { this.tickets = response.data.data || []; })
        .catch((error) => { console.error('Error fetching tickets:', error); this.tickets = []; })
        .finally(() => { this.ticketsLoading = false; });
    },
    // When the project changes in the form, the previously linked ticket no
    // longer applies, so clear it and reload the list for the new project.
    onFormProjectChange(projectId) {
      this.form.ticket_id = '';
      this.fetchTicketsForProject(projectId);
    },
    openCreate() {
      this.editing = false;
      this.form = this.blankForm();
      this.tickets = [];
      if (this.filters.project_id) {
        this.form.project_id = this.filters.project_id;
        this.fetchTicketsForProject(this.filters.project_id);
      }
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = true;
      this.fetchTicketsForProject(item.project_id);
      this.form = {
        id: item.id,
        project_id: item.project_id,
        ticket_id: item.ticket_id || '',
        title: item.title,
        preconditions: item.preconditions || '',
        steps: item.steps || '',
        expected_result: item.expected_result || '',
        priority: item.priority,
        assignee_id: item.assignee_id,
        due_date: item.due_date ? String(item.due_date).slice(0, 10) : null,
      };
      this.dialog = true;
    },
    save() {
      const url = this.editing ? this.constant.qa_update : this.constant.qa_create;
      const payload = { ...this.form };
      if (!this.editing) {
        delete payload.id;
        payload.created_by = this.currentUserLabel();
      } else {
        payload.updated_by = this.currentUserLabel();
      }
      axios.post(utils._api(url), payload, this.authHeaders())
        .then((response) => {
          this.dialog = false;
          this.notify(response.data.message);
          this.fetchCases();
        })
        .catch((error) => { console.error('Error saving test case:', error); this.notify(this.errorMessage(error, 'Failed to save')); });
    },
    remove() {
      if (!this.form.id) return;
      // Route the edit-dialog Delete through the same confirmation as the row action.
      this.askDelete({ id: this.form.id, title: this.form.title });
    },
    openTransfer(item) {
      this.transferTarget = item;
      this.transferDialog = true;
    },
    onTransferred(item, errorMsg) {
      if (errorMsg) { this.notify(errorMsg); return; }
      this.notify('Test case transferred');
      this.fetchCases();
    },
    askDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      axios.post(utils._api(this.constant.qa_delete), { id: this.deleteTarget.id }, this.authHeaders())
        .then((response) => {
          this.deleteDialog = false;
          this.deleteTarget = null;
          this.dialog = false;
          this.notify(response.data.message || 'Test case deleted');
          this.fetchCases();
        })
        .catch((error) => { console.error('Error deleting test case:', error); this.notify('Failed to delete'); })
        .finally(() => { this.deleting = false; });
    },
    openDetail(item) {
      this.detail = item;
      this.detailDialog = true;
      this.commentText = '';
      this.activity = [];
      this.loadExecutions(item.id);
      this.loadActivity(item.id);
    },
    loadExecutions(testCaseId) {
      this.executionsLoading = true;
      this.executions = [];
      axios.post(utils._api(this.constant.qa_list_executions), { test_case_id: testCaseId }, this.authHeaders())
        .then((response) => { this.executions = response.data.data || []; })
        .catch((error) => { console.error('Error fetching executions:', error); })
        .finally(() => { this.executionsLoading = false; });
    },
    loadActivity(testCaseId) {
      this.activityLoading = true;
      axios.post(utils._api(this.constant.qa_activity), { test_case_id: testCaseId }, this.authHeaders())
        .then((response) => { this.activity = response.data.data || []; })
        .catch((error) => { console.error('Error fetching activity:', error); })
        .finally(() => { this.activityLoading = false; });
    },
    addComment() {
      if (!this.commentText || !this.detail) return;
      axios.post(utils._api(this.constant.qa_add_comment), {
        test_case_id: this.detail.id,
        content: this.commentText,
        user: this.currentUserLabel(),
      }, this.authHeaders())
        .then(() => {
          this.commentText = '';
          this.loadActivity(this.detail.id);
        })
        .catch((error) => { console.error('Error adding comment:', error); this.notify('Failed to add comment'); });
    },
    openExecute(item) {
      this.executeForm = { test_case_id: item.id, title: item.title, result: 'Pass', notes: '' };
      this.executeDialog = true;
    },
    saveExecution() {
      axios.post(utils._api(this.constant.qa_add_execution), {
        test_case_id: this.executeForm.test_case_id,
        result: this.executeForm.result,
        notes: this.executeForm.notes,
        executed_by: this.currentUserLabel(),
      }, this.authHeaders())
        .then((response) => {
          this.executeDialog = false;
          this.notify(response.data.message);
          if (this.detailDialog && this.detail && this.detail.id === this.executeForm.test_case_id) {
            this.loadExecutions(this.executeForm.test_case_id);
            this.loadActivity(this.executeForm.test_case_id);
          }
          this.fetchCases();
        })
        .catch((error) => { console.error('Error recording execution:', error); this.notify('Failed to record execution'); });
    },
  },
  mounted() {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try { this.user_data = JSON.parse(userData); } catch (e) { this.user_data = null; }
    }
    this.getProjects();
    this.getEmployees();
    this.fetchCases();
  },
};
</script>

<style scoped>
.int-qa-table :deep(th) {
  font-weight: 700 !important;
  color: var(--int-text-strong) !important;
}
.int-qa-table :deep(td) {
  font-size: 0.85rem;
}
.qa-block {
  margin-bottom: var(--int-space-3);
}
.qa-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--int-text-muted);
  margin-bottom: var(--int-space-1);
}
.qa-text {
  font-size: 0.9rem;
  color: var(--int-text-strong);
}
.qa-timeline {
  max-height: 260px;
  overflow-y: auto;
}
</style>
